import React, { useState, useEffect, useRef } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonSpinner,
  IonText,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  IonSearchbar,
  IonBadge,
  IonFab,
  IonFabButton,
  IonIcon,
  IonImg,
  IonButton,
  IonChip,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { 
  camera, 
  hourglassOutline, 
  scanOutline, 
  checkmarkCircleOutline, 
  searchOutline, 
  linkOutline, 
  documentTextOutline, 
  checkmarkDoneOutline, 
  closeCircleOutline 
} from 'ionicons/icons';
import getApi from '../api';
import EnvironmentBadge from '../components/EnvironmentBadge';
import './IncomingGoods.css';

interface DeliveryNoteItem {
  item_id: string;
  description: string;
  quantity: number;
  unit: string;
}

interface AnalyzedData {
  supplier: string;
  delivery_note_number: string;
  date: string;
  items: DeliveryNoteItem[];
}

interface DeliveryNote {
  id: number;
  status: string;
  status_display: string;
  image: string;
  analyzed_data: AnalyzedData | null;
  matched_purchase_order_id: number | null;
  fortnox_goods_receipt_id: number | null;
  items: any[];
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

const IncomingGoods: React.FC = () => {
  const [deliveryNotes, setDeliveryNotes] = useState<DeliveryNote[]>([]);
  const [filteredNotes, setFilteredNotes] = useState<DeliveryNote[]>([]);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isUploading, setIsUploading] = useState(false);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const history = useHistory();
  const api = getApi();

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      'pending': 'primary',
      'analyzing': 'secondary',
      'analyzed': 'tertiary',
      'matching': 'warning',
      'matched': 'success',
      'creating_goods_receipt': 'warning',
      'completed': 'success',
      'failed': 'danger',
      'manually_corrected': 'medium'
    };
    return statusColors[status] || 'medium';
  };

  const getStatusIcon = (status: string): string => {
    const statusIcons: { [key: string]: string } = {
      'pending': hourglassOutline,
      'analyzing': scanOutline,
      'analyzed': checkmarkCircleOutline,
      'matching': searchOutline,
      'matched': linkOutline,
      'creating_goods_receipt': documentTextOutline,
      'completed': checkmarkDoneOutline,
      'failed': closeCircleOutline,
      'manually_corrected': checkmarkCircleOutline
    };
    return statusIcons[status] || hourglassOutline;
  };

  const isFinalStatus = (status: string): boolean => {
    return ['completed', 'failed', 'manually_corrected'].includes(status);
  };

  const hasNonFinalNotes = (): boolean => {
    return deliveryNotes.some(note => !isFinalStatus(note.status));
  };

  const loadDeliveryNotes = async (append: boolean = false, silent: boolean = false) => {
    try {
      if (!append && !silent) {
        setIsLoading(true);
      }
      if (!silent) {
        setError('');
      }
      
      let url = '/fortnox/delivery-notes/';
      const params = new URLSearchParams();
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      params.append('ordering', '-created_at');
      
      if (params.toString()) {
        url += '?' + params.toString();
      }
      
      const response = await api.get(url);
      console.log('Delivery notes:', response.data);
      
      let notes: DeliveryNote[] = [];
      if (response.data.results) {
        notes = response.data.results;
        setNextPage(response.data.next);
      } else if (Array.isArray(response.data)) {
        notes = response.data;
        setNextPage(null);
      }
      
      if (append) {
        setDeliveryNotes(prev => [...prev, ...notes]);
        setFilteredNotes(prev => [...prev, ...notes]);
      } else {
        setDeliveryNotes(notes);
        setFilteredNotes(notes);
      }
      
      // Start or stop polling based on status
      if (notes.some(note => !isFinalStatus(note.status))) {
        startPolling();
      } else {
        stopPolling();
      }
    } catch (error: any) {
      console.error('Failed to load delivery notes', error);
      if (!silent) {
        setError('Det gick inte att ladda följesedlar');
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  const startPolling = () => {
    // Don't start if already polling
    if (pollingIntervalRef.current) return;
    
    console.log('Starting list polling...');
    pollingIntervalRef.current = setInterval(() => {
      // Only poll if there are non-final notes
      if (hasNonFinalNotes()) {
        console.log('Polling for updates...');
        loadDeliveryNotes(false, true); // Silent reload
      } else {
        stopPolling();
      }
    }, 5000); // Poll every 5 seconds
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      console.log('Stopping list polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  useEffect(() => {
    loadDeliveryNotes();
    
    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [statusFilter]);

  useEffect(() => {
    if (!searchText || !searchText.trim()) {
      setFilteredNotes(deliveryNotes);
      return;
    }

    const search = searchText.toLowerCase();
    const filtered = deliveryNotes.filter(note => 
      note.analyzed_data?.supplier?.toLowerCase().includes(search) ||
      note.analyzed_data?.delivery_note_number?.toLowerCase().includes(search) ||
      note.id.toString().includes(search)
    );
    setFilteredNotes(filtered);
  }, [searchText, deliveryNotes]);

  const handleRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    loadDeliveryNotes().then(() => {
      event.detail.complete();
    });
  };

  const handleNoteClick = (id: number) => {
    history.push(`/IncomingGoods/${id}`);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleTakePhoto = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Vänligen välj en bildfil');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Bilden är för stor. Max 10MB tillåtet.');
        return;
      }

      // Compress image if needed
      const compressedBlob = await compressImage(file);
      await uploadDeliveryNote(compressedBlob);
      
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const compressImage = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Resize if width > 1920px
        if (width > 1920) {
          height = (height * 1920) / width;
          width = 1920;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          0.8
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      reader.readAsDataURL(file);
    });
  };

  const uploadDeliveryNote = async (imageBlob: Blob) => {
    try {
      setIsUploading(true);
      
      const formData = new FormData();
      formData.append('image', imageBlob, 'delivery-note.jpg');

      const response = await api.post('/fortnox/delivery-notes/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Upload response:', response.data);
      
      // Reload list to show new delivery note
      await loadDeliveryNotes();
      
      // Navigate to detail view
      if (response.data.id) {
        history.push(`/IncomingGoods/${response.data.id}`);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      alert('Det gick inte att ladda upp bilden: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  const loadMore = async (event: any) => {
    if (nextPage) {
      try {
        const response = await api.get(nextPage);
        const notes: DeliveryNote[] = response.data.results || [];
        setDeliveryNotes(prev => [...prev, ...notes]);
        setFilteredNotes(prev => [...prev, ...notes]);
        setNextPage(response.data.next);
      } catch (error) {
        console.error('Failed to load more', error);
      }
    }
    event.target.complete();
  };

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonBackButton defaultHref="/Home" />
          </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Följesedlar</IonTitle>
          <div slot="end" style={{ paddingRight: '8px' }}>
            <EnvironmentBadge />
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonRefresherContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
        </IonRefresher>

        <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonText placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <h2>Följesedlar</h2>
              <p>Ta bild på följesedel för automatisk registrering av inleveranser</p>
            </IonText>

            <IonSearchbar
              value={searchText}
              onIonChange={(e) => setSearchText(e.detail.value!)}
              placeholder="Sök leverantör, följesedelsnummer eller ID"
              debounce={300}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />

            {/* Status Filters */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px' }}>
              <IonChip
                color={statusFilter === 'all' ? 'primary' : 'medium'}
                onClick={() => setStatusFilter('all')}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Alla</IonLabel>
              </IonChip>
              <IonChip
                color={statusFilter === 'pending' ? 'primary' : 'medium'}
                onClick={() => setStatusFilter('pending')}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Väntande</IonLabel>
              </IonChip>
              <IonChip
                color={statusFilter === 'analyzing' ? 'secondary' : 'medium'}
                onClick={() => setStatusFilter('analyzing')}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Analyserar</IonLabel>
              </IonChip>
              <IonChip
                color={statusFilter === 'completed' ? 'success' : 'medium'}
                onClick={() => setStatusFilter('completed')}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Klara</IonLabel>
              </IonChip>
              <IonChip
                color={statusFilter === 'failed' ? 'danger' : 'medium'}
                onClick={() => setStatusFilter('failed')}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Misslyckade</IonLabel>
              </IonChip>
            </div>

            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <IonSpinner name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                <p>Laddar följesedlar...</p>
              </div>
            ) : error ? (
              <IonText color="danger" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <p>{error}</p>
              </IonText>
            ) : filteredNotes.length === 0 ? (
              <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <p style={{ textAlign: 'center', padding: '20px' }}>
                  {searchText ? 'Inga följesedlar matchade sökningen' : 'Inga följesedlar ännu'}
                </p>
                <p style={{ textAlign: 'center' }}>
                  Tryck på kameraknappen för att ta bild på en följesedel
                </p>
              </IonText>
            ) : (
              <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                {filteredNotes.map((note) => (
                  <IonItem
                    key={note.id}
                    button
                    onClick={() => handleNoteClick(note.id)}
                    placeholder={undefined}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  >
                    {note.image && (
                      <IonImg 
                        src={note.image} 
                        alt="Följesedel" 
                        style={{ width: '60px', height: '60px', objectFit: 'cover', marginRight: '12px', borderRadius: '4px' }}
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    )}
                    <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <h3>
                        <strong>
                          {note.analyzed_data?.delivery_note_number 
                            ? `Följesedel ${note.analyzed_data.delivery_note_number}` 
                            : `Följesedel #${note.id}`}
                        </strong>
                      </h3>
                      {note.analyzed_data?.supplier && (
                        <p>{note.analyzed_data.supplier}</p>
                      )}
                      <p>
                        <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          {formatDate(note.created_at)}
                        </IonText>
                      </p>
                      <div style={{ marginTop: '8px' }}>
                        <IonBadge color={getStatusColor(note.status)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <IonIcon icon={getStatusIcon(note.status)} style={{ marginRight: '4px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                          {note.status_display}
                        </IonBadge>
                      </div>
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>
            )}
          </IonCardContent>
        </IonCard>

        <IonInfiniteScroll onIonInfinite={loadMore} disabled={!nextPage} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonInfiniteScrollContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
        </IonInfiniteScroll>

        {/* Hidden file input for camera */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {/* Floating Action Button for Camera */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonFabButton onClick={handleTakePhoto} disabled={isUploading} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {isUploading ? (
              <IonSpinner name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            ) : (
              <IonIcon icon={camera} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            )}
          </IonFabButton>
        </IonFab>
      </IonContent>
    </IonPage>
  );
};

export default IncomingGoods;
